import { useState } from 'react'
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'

import { useConnectionDetail } from '../hooks/useAdminConnections'
import { triggerRun, updateConnection } from '../service/adminConnectionsService'
import {
  ACTIVE_RUN_STATUSES,
  RosterConnectionInfo,
  RosterSyncRun,
  RosterSyncRunStatus,
} from '../types'
import RunDetailModal from './RunDetailModal'

const STATUS_COLORS: Record<RosterSyncRunStatus, string> = {
  pending: 'yellow',
  processing: 'blue',
  completed: 'green',
  completed_with_errors: 'orange',
  failed: 'red',
  cancelled: 'gray',
}

interface Props {
  opened: boolean
  connection: RosterConnectionInfo | null
  onClose: () => void
  onChanged: () => void
}

const ConnectionDrawer = ({ opened, connection: listedConnection, onClose, onChanged }: Props) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [detailRunId, setDetailRunId] = useState<string | null>(null)
  const connectionId = listedConnection?.id ?? null
  const { connection, isLoading, refetchConnection } = useConnectionDetail(connectionId)

  const notifySuccess = (message: string) =>
    notifications.show({ message, color: 'green', icon: <IconCheck size={18} /> })
  const notifyError = () =>
    notifications.show({
      message: t('admin_connections.error_generic'),
      color: 'red',
      icon: <IconX size={18} />,
    })

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    setSaving(true)
    try {
      await action()
      notifySuccess(successMessage)
      refetchConnection()
      onChanged()
    } catch {
      notifyError()
    } finally {
      setSaving(false)
    }
  }

  const handleSettingsSave = (values: {
    enabled: boolean
    archiveEnabled: boolean
    archiveThresholdPercent: number
  }) => {
    if (!connectionId) return
    runAction(() => updateConnection(connectionId, values), t('admin_connections.saved'))
  }

  const handleTrigger = (dryRun: boolean, forceArchive = false) => {
    if (!connectionId) return
    runAction(
      () => triggerRun(connectionId, { dryRun, forceArchive }),
      t('admin_connections.sync_started'),
    )
  }

  const confirmForceArchive = () =>
    modals.openConfirmModal({
      title: t('admin_connections.force_archive_confirm_title'),
      children: <Text size='sm'>{t('admin_connections.force_archive_confirm')}</Text>,
      labels: {
        confirm: t('admin_connections.force_archive'),
        cancel: t('admin_connections.cancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => handleTrigger(false, true),
    })

  const hasActiveRun = connection?.recentRuns?.some(run =>
    ACTIVE_RUN_STATUSES.includes(run.status),
  )

  const districtLabel =
    listedConnection?.districtName || listedConnection?.districtExternalId || ''

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position='right'
      size='xl'
      title={
        <Title order={3}>
          {t('admin_connections.connection_title', { district: districtLabel })}
        </Title>
      }
    >
      {!listedConnection ? null : isLoading || !connection ? (
        <Group position='center' py='xl'>
          <Loader />
        </Group>
      ) : (
        <Stack spacing='md'>
          <Group spacing='xs'>
            <Badge color={connection.enabled ? 'green' : 'gray'}>
              {connection.enabled
                ? t('admin_connections.enabled')
                : t('admin_connections.disabled')}
            </Badge>
            <Text size='sm' color='dimmed'>
              {t('admin_connections.district_id')}: {connection.districtExternalId}
            </Text>
          </Group>

          <ConnectionSettingsForm
            key={connection.id}
            enabled={connection.enabled}
            archiveEnabled={connection.archiveEnabled}
            archiveThresholdPercent={connection.archiveThresholdPercent}
            saving={saving}
            onSave={handleSettingsSave}
          />

          <Divider />

          <Group>
            <Button
              variant='default'
              disabled={saving || hasActiveRun}
              onClick={() => handleTrigger(true)}
            >
              {t('admin_connections.dry_run')}
            </Button>
            <Button disabled={saving || hasActiveRun} onClick={() => handleTrigger(false)}>
              {t('admin_connections.sync_now')}
            </Button>
            <Button
              color='red'
              variant='outline'
              disabled={saving || hasActiveRun}
              onClick={confirmForceArchive}
            >
              {t('admin_connections.force_archive')}
            </Button>
            {hasActiveRun && (
              <Group spacing='xs'>
                <Loader size='xs' />
                <Text size='sm'>{t('admin_connections.sync_in_progress')}</Text>
              </Group>
            )}
          </Group>

          <Divider />

          <Title order={4}>{t('admin_connections.runs_title')}</Title>
          {connection.recentRuns?.length ? (
            <>
              <Text size='xs' color='dimmed'>
                {t('admin_connections.runs_hint')}
              </Text>
              <RunsTable runs={connection.recentRuns} onSelect={setDetailRunId} />
            </>
          ) : (
            <Text size='sm' color='dimmed'>
              {t('admin_connections.no_runs')}
            </Text>
          )}

          <RunDetailModal runId={detailRunId} onClose={() => setDetailRunId(null)} />
        </Stack>
      )}
    </Drawer>
  )
}

interface SettingsProps {
  enabled: boolean
  archiveEnabled: boolean
  archiveThresholdPercent: number
  saving: boolean
  onSave: (values: {
    enabled: boolean
    archiveEnabled: boolean
    archiveThresholdPercent: number
  }) => void
}

const ConnectionSettingsForm = ({
  enabled,
  archiveEnabled,
  archiveThresholdPercent,
  saving,
  onSave,
}: SettingsProps) => {
  const { t } = useTranslation()
  const form = useForm({ initialValues: { enabled, archiveEnabled, archiveThresholdPercent } })

  return (
    <form onSubmit={form.onSubmit(onSave)}>
      <Stack spacing='sm'>
        <Switch
          label={t('admin_connections.enabled')}
          {...form.getInputProps('enabled', { type: 'checkbox' })}
        />
        <Checkbox
          label={t('admin_connections.archive_enabled')}
          {...form.getInputProps('archiveEnabled', { type: 'checkbox' })}
        />
        <NumberInput
          label={t('admin_connections.archive_threshold')}
          min={0}
          max={100}
          {...form.getInputProps('archiveThresholdPercent')}
        />
        <Group position='right'>
          <Button type='submit' variant='light' loading={saving}>
            {t('admin_connections.save')}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

interface RunsTableProps {
  runs: RosterSyncRun[]
  onSelect: (runId: string) => void
}

const RunsTable = ({ runs, onSelect }: RunsTableProps) => {
  const { t } = useTranslation()

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString() : '—'

  const summarizeCounters = (counters: Record<string, number>) =>
    Object.entries(counters)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => `${key}: ${count}`)
      .join(' · ')

  return (
    <Table striped highlightOnHover>
      <thead>
        <tr>
          <th>{t('admin_connections.run_status')}</th>
          <th>{t('admin_connections.run_type')}</th>
          <th>{t('admin_connections.run_started')}</th>
          <th>{t('admin_connections.run_counters')}</th>
        </tr>
      </thead>
      <tbody>
        {runs.map(run => (
          <tr key={run.id} onClick={() => onSelect(run.id)} style={{ cursor: 'pointer' }}>
            <td>
              <Badge color={STATUS_COLORS[run.status] ?? 'gray'}>
                {t(`admin_connections.status.${run.status}`)}
              </Badge>
            </td>
            <td>
              {run.dryRun
                ? t('admin_connections.dry_run')
                : t('admin_connections.full_sync')}
            </td>
            <td>{formatDate(run.startedAt ?? run.createdAt)}</td>
            <td>
              <Text size='xs' color='dimmed' style={{ maxWidth: 320 }} lineClamp={2}>
                {summarizeCounters(run.counters) || '—'}
              </Text>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

export default ConnectionDrawer
